import { useMemo, useState } from "react";

import InputText from "@/design/forms/InputText";
import Valkyrie, { viEye, viEyeSlash } from "@sippy-platform/valkyrie";

import { LinearProgress, Stack, Typography } from "@mui/material";
import { amber, green, orange, red } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { passwordStrength } from "check-password-strength";
import PropTypes from "prop-types";

function InputPassword({ showPassword, showPasswordStrength = false, id, value = "", ...props }) {
  const [show, setShow] = useState(showPassword);
  const [previousShowPassword, setPreviousShowPassword] = useState(showPassword);

  if (showPassword !== previousShowPassword) {
    setPreviousShowPassword(showPassword);
    setShow(showPassword);
  }

  const pwStrength = useMemo(() => {
    if (value.length >= 8) {
      return passwordStrength(value);
    }

    return false;
  }, [value]);

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <InputText
        {...props}
        value={value}
        id={id}
        type={show ? "text" : "password"}
        slotProps={{
          input: {
            sx: { pr: 0.625 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton tabIndex={-1} onClick={() => setShow(!show)} sx={{ p: 1, borderRadius: 0.5 }}>
                  <Valkyrie icon={show ? viEyeSlash : viEye} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      {showPasswordStrength && (
        <Stack>
          <LinearProgress
            variant="determinate"
            size="small"
            value={pwStrength ? Math.max(((pwStrength.id + 1) / 4) * 100) : 0}
            sx={(theme) => ({
              bgcolor: theme.vars.palette.grey[300],
              ".MuiLinearProgress-bar": {
                bgcolor: pwStrength
                  ? pwStrength.id === 0
                    ? red[500]
                    : pwStrength.id === 1
                      ? orange[500]
                      : pwStrength.id === 2
                        ? amber[500]
                        : green[500]
                  : red[500],
              },
            })}
          />
          <Typography sx={{ fontSize: "12px", alignSelf: "flex-end" }}>{pwStrength?.value ?? "Too short"}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

InputPassword.propTypes = {
  id: PropTypes.string,
  showPassword: PropTypes.bool,
  showPasswordStrength: PropTypes.bool,
  value: PropTypes.string,
};

export default InputPassword;
